import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Button, message, Modal, Radio, Input, Space, Spin, Alert } from 'antd';
import { FileDoneOutlined, DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DashboardLayout } from '../components/layout';
import { RecommendationCanvas, TemplatePalette } from '../components/recommendations';
import { LAYOUT_CONFIGS } from '../components/recommendations/RecommendationCanvas';
import {
  listRecommendationTemplates,
  saveReportWithRecommendations,
  generateRecommendationsPdf,
  downloadReportPdf,
  getSavedReport,
} from '../services/api/odinApi';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl || '2rem'};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div``;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm || '0.5rem'};
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg || '1.5rem'};
  height: calc(100vh - 200px);
`;

const CanvasColumn = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--odin-border-strong);
    border-radius: 3px;
  }
`;

const PaletteColumn = styled.div`
  width: 280px;
  flex-shrink: 0;
`;

const TIMEFRAME_OPTIONS = [
  { label: '0-3 Months', value: '0-3months' },
  { label: '3-6 Months', value: '3-6months' },
  { label: '6-12 Months', value: '6-12months' },
  { label: 'Custom', value: 'custom' },
];

const RecommendationsPage = () => {
  // Parse query params for reportId, basePdfId, customer
  const params = new URLSearchParams(window.location.search);
  const reportId = params.get('reportId');
  const customerName = params.get('customer') || 'Customer';

  // State
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [pages, setPages] = useState([1]);
  const [placements, setPlacements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(!!reportId);
  const [pdfExpired, setPdfExpired] = useState(false);
  const [layout, setLayout] = useState('2x3');

  // Timeframe modal state
  const [timeframeModal, setTimeframeModal] = useState({
    open: false,
    template: null,
    pageNumber: null,
    zoneNumber: null,
    timeframe: '0-3months',
    timeframeCustom: '',
    timeframeDisabled: false,
    budget: '',
  });

  // DnD sensor
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const result = await listRecommendationTemplates();
        setTemplates(result?.templates || []);
      } catch (err) {
        console.error('Failed to load templates:', err);
        message.error('Failed to load recommendation templates');
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
  }, []);

  // Load existing recommendations when reportId is present
  // Waits for templates to load first so we can resolve display info
  useEffect(() => {
    if (!reportId || templatesLoading) return;
    const loadExisting = async () => {
      try {
        const result = await getSavedReport(reportId);
        const report = result?.report;
        // Restore layout
        if (report?.layout) {
          setLayout(report.layout);
        }

        if (report?.recommendations?.length > 0) {
          const recs = report.recommendations;
          // Build template lookup for display info
          const templateMap = {};
          templates.forEach((t) => { templateMap[t.id] = t; });

          setPlacements(
            recs.map((r) => {
              const tmpl = templateMap[r.template_id];
              return {
                template_id: r.template_id,
                page_number: r.page_number,
                zone_number: r.zone_number,
                timeframe: r.timeframe,
                timeframe_custom: r.timeframe_custom,
                variable_values: r.variable_values || {},
                title: tmpl?.title || 'Recommendation',
                icon: tmpl?.icon || null,
                platform: tmpl?.platform || 'general',
              };
            })
          );
          // Restore pages from max page_number
          const maxPage = Math.max(...recs.map((r) => r.page_number));
          setPages(Array.from({ length: maxPage }, (_, i) => i + 1));
        }
      } catch (err) {
        console.error('Failed to load existing recommendations:', err);
      } finally {
        setLoadingExisting(false);
      }
    };
    loadExisting();
  }, [reportId, templatesLoading, templates]);

  // Handle drag end — show timeframe modal
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;

    const template = active.data.current?.template;
    const { pageNumber, zoneNumber } = over.data.current || {};

    if (!template || !pageNumber || !zoneNumber) return;

    // Check if page already has a timeframe
    const pagePlacements = placements.filter(p => p.page_number === pageNumber);
    const pageTimeframe = pagePlacements.length > 0 ? pagePlacements[0].timeframe : null;
    const pageTimeframeCustom = pagePlacements.length > 0 ? pagePlacements[0].timeframe_custom : null;

    // Open timeframe selection modal
    setTimeframeModal({
      open: true,
      template,
      pageNumber,
      zoneNumber,
      timeframe: pageTimeframe || '0-3months',  // Pre-fill if page has timeframe
      timeframeCustom: pageTimeframeCustom || '',
      timeframeDisabled: !!pageTimeframe,  // Disable if page has timeframe
      budget: '',
    });
  }, [placements]);

  // Confirm timeframe and place template
  const handleTimeframeConfirm = useCallback(() => {
    const { template, pageNumber, zoneNumber, timeframe, timeframeCustom, budget } = timeframeModal;

    // Validate budget
    if (!budget || budget.trim() === '') {
      message.warning('Please enter a budget amount');
      return;
    }

    setPlacements((prev) => {
      // Remove existing placement in this zone
      const filtered = prev.filter(
        (p) => !(p.page_number === pageNumber && p.zone_number === zoneNumber)
      );
      return [
        ...filtered,
        {
          template_id: template.id,
          page_number: pageNumber,
          zone_number: zoneNumber,
          timeframe,
          timeframe_custom: timeframe === 'custom' ? timeframeCustom : null,
          variable_values: { budget },
          // Display info
          title: template.title,
          icon: template.icon,
          platform: template.platform,
        },
      ];
    });

    setTimeframeModal({
      open: false,
      template: null,
      pageNumber: null,
      zoneNumber: null,
      timeframe: '0-3months',
      timeframeCustom: '',
      timeframeDisabled: false,
      budget: '',
    });
  }, [timeframeModal]);

  // Remove placement
  const handleRemovePlacement = useCallback((pageNumber, zoneNumber) => {
    setPlacements((prev) =>
      prev.filter(
        (p) => !(p.page_number === pageNumber && p.zone_number === zoneNumber)
      )
    );
  }, []);

  // Add/remove pages
  const handleAddPage = useCallback(() => {
    setPages((prev) => {
      if (prev.length >= 3) return prev;
      const next = Math.max(...prev) + 1;
      return [...prev, next];
    });
  }, []);

  const handleRemovePage = useCallback((pageNum) => {
    setPages((prev) => prev.filter((p) => p !== pageNum));
    setPlacements((prev) => prev.filter((p) => p.page_number !== pageNum));
  }, []);

  // Change grid layout — warn if placements would be lost
  const handleLayoutChange = useCallback((newLayout) => {
    const newZones = LAYOUT_CONFIGS[newLayout]?.zones ?? 6;
    const outOfBounds = placements.filter((p) => p.zone_number > newZones);
    if (outOfBounds.length > 0) {
      Modal.confirm({
        title: 'Switch grid layout?',
        content: `Switching to ${newLayout} layout will remove ${outOfBounds.length} recommendation(s) beyond zone ${newZones}. Continue?`,
        okText: 'Switch layout',
        okType: 'danger',
        onOk: () => {
          setPlacements((prev) => prev.filter((p) => p.zone_number <= newZones));
          setLayout(newLayout);
        },
      });
    } else {
      setLayout(newLayout);
    }
  }, [placements]);

  // Save recommendations
  const handleSave = useCallback(async () => {
    if (!reportId) {
      message.warning('No report ID — generate an MSR report first');
      return;
    }
    if (placements.length === 0) {
      message.warning('No recommendations placed');
      return;
    }

    setSaving(true);
    try {
      await saveReportWithRecommendations(
        reportId,
        placements.map(({ template_id, page_number, zone_number, timeframe, timeframe_custom, variable_values }) => ({
          template_id,
          page_number,
          zone_number,
          timeframe,
          timeframe_custom,
          variable_values,
        })),
        layout
      );
      message.success('Recommendations saved');
    } catch (err) {
      console.error('Save failed:', err);
      message.error('Failed to save recommendations');
    } finally {
      setSaving(false);
    }
  }, [reportId, placements]);

  // Generate PDF
  const handleGenerate = useCallback(async () => {
    if (!reportId) {
      message.warning('No report ID');
      return;
    }

    // Save first, then generate
    setGenerating(true);
    try {
      await handleSave();
      const result = await generateRecommendationsPdf(reportId);

      // Handle API-level failure (success: false returned as HTTP 200 without throwing)
      if (result && result.success === false) {
        const errMsg = result.error || 'Failed to generate PDF';
        console.error('Generate failed (API error):', errMsg);
        if (errMsg.includes('BASE_PDF_EXPIRED') || errMsg.includes('expired') || errMsg.includes('not found in cache')) {
          setPdfExpired(true);
          message.error('The base MSR PDF has expired. Please regenerate the report first.');
        } else {
          message.error(errMsg);
        }
        return;
      }

      if (result?.pdfId) {
        message.success(`PDF generated (${result.pageCount} pages)`);
        // Trigger download
        await downloadReportPdf(result.pdfId, `${customerName}-MSR-Recommendations.pdf`);
      }
    } catch (err) {
      console.error('Generate failed:', err);
      const errMsg = err?.message || err?.toString() || '';
      if (errMsg.includes('BASE_PDF_EXPIRED') || errMsg.includes('expired') || errMsg.includes('not found in cache')) {
        setPdfExpired(true);
        message.error('The base MSR PDF has expired. Please regenerate the report first.');
      } else {
        message.error('Failed to generate PDF');
      }
    } finally {
      setGenerating(false);
    }
  }, [reportId, customerName, handleSave]);

  return (
    <DashboardLayout>
      <PageHeader>
        <HeaderLeft>
          <Title level={3} style={{ margin: 0, color: 'var(--odin-text-primary)' }}>
            <FileDoneOutlined style={{ marginRight: 8, color: 'var(--odin-accent)' }} />
            Strategic Recommendations
          </Title>
          <Text style={{ color: 'var(--odin-text-secondary)' }}>
            {customerName} &mdash; Drag templates to build recommendation pages
          </Text>
        </HeaderLeft>
        <HeaderActions>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!reportId || placements.length === 0}
          >
            Save
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleGenerate}
            loading={generating}
            disabled={!reportId || placements.length === 0}
          >
            Generate PDF
          </Button>
        </HeaderActions>
      </PageHeader>

      {pdfExpired && (
        <Alert
          type="warning"
          showIcon
          message="Base PDF Expired"
          description="The base MSR PDF has expired. Please regenerate the report on the Reports page before generating a merged PDF."
          action={
            <Button
              size="small"
              onClick={() => window.location.href = '/reports'}
            >
              Go to Reports
            </Button>
          }
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setPdfExpired(false)}
        />
      )}

      {loadingExisting ? (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <Spin size="large" tip="Loading saved recommendations..." />
        </div>
      ) : (
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <ContentWrapper>
          <CanvasColumn>
            <RecommendationCanvas
              pages={pages}
              placements={placements}
              onRemovePlacement={handleRemovePlacement}
              onAddPage={handleAddPage}
              onRemovePage={handleRemovePage}
              layout={layout}
              onLayoutChange={handleLayoutChange}
            />
          </CanvasColumn>
          <PaletteColumn>
            <TemplatePalette
              templates={templates}
              loading={templatesLoading}
            />
          </PaletteColumn>
        </ContentWrapper>
      </DndContext>
      )}

      {/* Timeframe Selection Modal */}
      <Modal
        title="Recommendation Details"
        open={timeframeModal.open}
        onOk={handleTimeframeConfirm}
        onCancel={() => setTimeframeModal((prev) => ({ ...prev, open: false }))}
        okText="Place Recommendation"
        width={500}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Recommendation: <Text style={{ color: 'var(--odin-accent)' }}>{timeframeModal.template?.title}</Text>
            </Text>
          </div>

          {/* Info banner when timeframe is locked */}
          {timeframeModal.timeframeDisabled && (
            <Alert
              type="info"
              showIcon
              message={`Page timeline: ${TIMEFRAME_OPTIONS.find(
                opt => opt.value === timeframeModal.timeframe
              )?.label || 'Custom'}`}
              description="All recommendations on a page must share the same timeline. To change it, remove all cards from this page first."
              style={{ marginBottom: 0 }}
            />
          )}

          {/* Timeframe Section */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Implementation Timeframe
            </Text>
            <Radio.Group
              value={timeframeModal.timeframe}
              onChange={(e) =>
                setTimeframeModal((prev) => ({ ...prev, timeframe: e.target.value }))
              }
              optionType="button"
              buttonStyle="solid"
              options={TIMEFRAME_OPTIONS}
              style={{ width: '100%' }}
              disabled={timeframeModal.timeframeDisabled}
            />
            {timeframeModal.timeframe === 'custom' && (
              <Input
                placeholder="e.g., After Q2 audit"
                value={timeframeModal.timeframeCustom}
                onChange={(e) =>
                  setTimeframeModal((prev) => ({
                    ...prev,
                    timeframeCustom: e.target.value,
                  }))
                }
                style={{ marginTop: '8px' }}
                disabled={timeframeModal.timeframeDisabled}
              />
            )}
          </div>

          {/* Budget Section */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Estimated Budget <Text type="danger">*</Text>
            </Text>
            <Input
              placeholder="Enter budget amount (e.g., 5000)"
              value={timeframeModal.budget}
              onChange={(e) => {
                setTimeframeModal((prev) => ({
                  ...prev,
                  budget: e.target.value,
                }));
              }}
              prefix="£"
              type="text"
              size="large"
              style={{
                width: '100%',
                fontSize: '16px',
              }}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '6px', display: 'block' }}>
              Enter the estimated budget for implementing this recommendation
            </Text>
          </div>
        </Space>
      </Modal>
    </DashboardLayout>
  );
};

export default RecommendationsPage;
